import React from 'react'
import { useLocation, useNavigate, matchPath } from 'react-router-dom'
import {
	useAppDispatch,
	useAppSelector,
	authActions,
	ConfigStateType,
	CrudType,
	UserMenuAccessType,
	MenuType,
} from '@redux/index'
import config, { SidePageType } from '@configs'

interface PropsType {
	children: JSX.Element
}

export const AccessControlGuard = (props: PropsType) => {
	// hook
	const location = useLocation()
	const navigate = useNavigate()

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const mode: ConfigStateType['mode'] = useAppSelector(state => state.config.mode)
	const userAccessMenuList: UserMenuAccessType[] = useAppSelector(
		state => state.auth.userAccessMenuList,
	)
	const adminMenu: MenuType[] = useAppSelector(state => state.admin.menu)

	// state
	const [isAuthValidate, setIsAuthValidate] = React.useState<boolean>(true)

	// variables
	const sidePageList: SidePageType[] = config.sidePage()
	const isAdmin = React.useMemo(() => {
		return location.pathname.startsWith('/admin')
	}, [location.pathname])

	React.useEffect(() => {
		const crud: CrudType = getAccessControlCRUD()

		if (crud.includes('R')) {
			setIsAuthValidate(true)
		} else {
			setIsAuthValidate(false)
		}

		reduxDispatch(authActions.CURRENT_ACCESS(crud))
		// 권한 목록이 바뀌면(폴링·화면 이동으로 갱신) 지금 열려 있는 화면도 다시 검사한다 —
		// 권한을 뺏긴 사람이 가만히 있어도 홈으로 나간다.
	}, [isAuthValidate, location.pathname, userAccessMenuList])

	React.useEffect(() => {
		if (!isAuthValidate) {
			if (mode === 'main') {
				navigate('/main/home')
			} else {
				navigate('/admin/home')
			}
		}
	}, [isAuthValidate, location.pathname])

	const getAccessControlCRUD = React.useCallback((): CrudType => {
		// 메뉴 권한과 무관하게 열리는 페이지. My Page 는 자기 계정을 고치는 자리라
		// 특정 메뉴의 CRUD 를 따를 이유가 없다 — 로그인한 스태프면 누구나 들어간다.
		const defaultPageUris: string[] = [
			'/main/home',
			'/admin/home',
			'/main/mypage',
			'/admin/mypage',
		]
		if (defaultPageUris.includes(location.pathname)) {
			return ['R']
		}

		const thisMenuRes = getThisMenuIdAndType()

		if (thisMenuRes.type === 'side') {
			const thisSidePageArr = sidePageList.filter(
				aSidePage => aSidePage.id === thisMenuRes.id,
			)

			const isValidSidePage: boolean =
				thisSidePageArr.length > 0 && thisSidePageArr[0].parentPath !== ''

			if (isValidSidePage) {
				// 하위 페이지는 자기 권한을 갖지 않고 부모 메뉴의 CRUD 를 그대로 따른다.
				// admin 메뉴도 userAccessMenuList 에 `uri = adminMenu[].to` 로 들어 있어
				// 같은 비교가 그대로 성립한다(AccessControlManager.getUserMenuAccessList).
				const parentMenuArr = userAccessMenuList.filter(
					aUserAccessMenu => aUserAccessMenu.uri === thisSidePageArr[0].parentPath,
				)

				const res2: CrudType =
					parentMenuArr.length > 0 ? (parentMenuArr[0].crud.split('') as CrudType) : []

				return res2
			}

			return []
		} else {
			const index = userAccessMenuList.findIndex(aUserAccessMenu => {
				return aUserAccessMenu.menuId === thisMenuRes.id
			})

			const res: CrudType =
				index === -1 ? [] : (userAccessMenuList[index].crud.split('') as CrudType)

			return res
		}
	}, [mode, userAccessMenuList, location.pathname])

	const getThisMenuIdAndType = React.useCallback((): {
		id: string
		type?: 'admin' | 'main' | 'side'
	} => {
		if (isAdmin) {
			// 최상위 메뉴 매칭
			const thisAdminMenu = adminMenu.filter(anAdminMenu => {
				const aSidePageTo: string = anAdminMenu.to ?? ''
				const matchRes = matchPath(aSidePageTo, location.pathname)
				return matchRes !== null
			})

			if (thisAdminMenu.length > 0) {
				return { id: thisAdminMenu[0].id, type: 'admin' }
			}

			// children 메뉴 매칭 → 부모 메뉴 ID 반환
			const parentMenu = adminMenu.find(anAdminMenu => {
				if (!anAdminMenu.children) return false
				return anAdminMenu.children.some(child => {
					const childTo: string = child.to ?? ''
					return matchPath(childTo, location.pathname) !== null
				})
			})

			if (parentMenu) {
				return { id: parentMenu.id, type: 'admin' }
			}

			// 전체 화면 하위 페이지(FullModalLayout)는 메뉴가 아니라 sidePageList 에 있다.
			// 이걸 안 보면 /admin/reset-password · /admin/user-profile-image-update 가
			// id '' 로 떨어져 CRUD 가 빈 배열이 되고, 열자마자 /admin/home 으로 튕긴다.
			// (main 쪽 분기는 원래 sidePageList 를 보고 있었다 — admin 쪽만 빠져 있었다)
			const thisAdminSidePage = sidePageList.filter(aSidePage => {
				const aSidePageTo: string = aSidePage.to ?? ''
				return matchPath(aSidePageTo, location.pathname) !== null
			})

			if (thisAdminSidePage.length > 0) {
				return { id: thisAdminSidePage[0].id, type: 'side' }
			}

			return { id: '' }
		} else {
			const thisSidePage = sidePageList.filter(aSidePage => {
				const aSidePageTo: string = aSidePage.to ?? ''
				const matchRes = matchPath(aSidePageTo, location.pathname)
				const isMatched = matchRes !== null

				return isMatched
			})
			const thisMenuPage = userAccessMenuList.filter(aMainPage => {
				const aSidePageTo: string = aMainPage.uri ?? ''
				const matchRes = matchPath(aSidePageTo, location.pathname)
				const isMatched = matchRes !== null

				return isMatched
			})

			if (thisMenuPage.length > 0) {
				return { id: thisMenuPage[0].menuId, type: 'main' }
			} else if (thisSidePage.length > 0) {
				return { id: thisSidePage[0].id, type: 'side' }
			} else {
				return { id: '' }
			}
		}
	}, [isAdmin, location.pathname, adminMenu, sidePageList])

	return <>{props.children}</>
}
